import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';

import { useChatContext } from 'contexts/ChatContext';
import { feedbackPrompt } from 'network/ApiAxios';

const RATINGS = [1, 2, 3, 4, 5];
const THANKS_VISIBLE_MS = 3000;
const CARD_CLASS = 'feedback-prompt w-75 mx-auto mb-2 p-3 bg-white border rounded-3 shadow-sm';

// The one-time "how is Bevan doing?" card shown above the chat composer.
// When and whether it appears is decided in ChatContext / the backend.
const FeedbackPrompt = () => {
    const { isFeedbackPromptVisible, hideFeedbackPrompt } = useChatContext();
    const [rating, setRating] = useState(null);
    const [hoverRating, setHoverRating] = useState(null);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isThanked, setIsThanked] = useState(false);
    const [error, setError] = useState('');
    const thanksTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(thanksTimerRef.current), []);

    if (!isFeedbackPromptVisible) return null;

    const dismiss = () => {
        hideFeedbackPrompt();
        feedbackPrompt('post', { action: 'dismiss' }).catch(() => {});
    };

    const submit = async () => {
        if (!rating || isSubmitting) return;
        setIsSubmitting(true);
        setError('');
        try {
            await feedbackPrompt('post', { action: 'submit', rating, comment: comment.trim() });
            setIsThanked(true);
            thanksTimerRef.current = setTimeout(hideFeedbackPrompt, THANKS_VISIBLE_MS);
        } catch (err) {
            setError('Sorry, we couldn\'t send that. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isThanked) {
        return (
            <div className={`${CARD_CLASS} text-center`} role="status">
                <span className="text-sm text-dark">Thanks for your feedback! It helps us improve Bevan.</span>
            </div>
        );
    }

    const shownRating = hoverRating ?? rating;

    return (
        <div
            className={`${CARD_CLASS} position-relative`}
            role="region"
            aria-label="Feedback"
        >
            <button
                type="button"
                className="btn btn-link p-0 m-0 position-absolute top-0 end-0 mt-2 me-3 text-secondary shadow-none"
                aria-label="Close feedback"
                onClick={dismiss}
            >
                <i className="fa-solid fa-xmark"></i>
            </button>

            <p className="text-sm font-weight-bold text-dark mb-2 pe-4">How is Bevan working for you so far?</p>

            <div className="d-flex align-items-center mb-2" role="group" aria-label="Rating">
                {RATINGS.map(value => (
                    <button
                        key={value}
                        type="button"
                        className="btn btn-link p-0 m-0 me-2 shadow-none"
                        aria-label={`Rate ${value} out of 5`}
                        aria-pressed={rating === value}
                        onClick={() => setRating(value)}
                        onMouseEnter={() => setHoverRating(value)}
                        onMouseLeave={() => setHoverRating(null)}
                    >
                        <i
                            className={`fa-star fa-lg ${shownRating && value <= shownRating ? 'fa-solid text-warning' : 'fa-regular text-secondary'}`}
                        ></i>
                    </button>
                ))}
            </div>

            <textarea
                className="form-control text-sm mb-2"
                rows="2"
                maxLength={5000}
                placeholder="Anything we could do better? (optional)"
                aria-label="Feedback comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
            />

            {error && <p className="text-xs text-danger mb-2">{error}</p>}

            <div className="d-flex justify-content-end">
                <Button type="button" color="link" size="sm" className="mb-0 me-2 text-secondary shadow-none" onClick={dismiss}>
                    Maybe later
                </Button>
                <Button type="button" color="dark" size="sm" className="mb-0" disabled={!rating || isSubmitting} onClick={submit}>
                    {isSubmitting ? 'Sending…' : 'Send'}
                </Button>
            </div>
        </div>
    );
};

export default FeedbackPrompt;
